import { useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import { Box, Button, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Modal, IconButton } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { convertFromHtml, modalStyle } from '../../constants'

const FSC_COLORS = {
  LOW_RISK: '#52803c',
  HIGH_RISK: '#d54559',
} as const;

function generateRiskLevelStyles(indicators: any[], highRiskLevels: string[]) {
  const uniqueRiskLevels = Array.from(
    new Set<string>(indicators.map((ind: any) => ind?.fsc_risk_level_option.risk_level).filter((value: string | null) => value))
  );

  return Object.fromEntries(
    uniqueRiskLevels.map((riskLevel: string) => {
      const backgroundColor = highRiskLevels.includes(riskLevel)
        ? FSC_COLORS.HIGH_RISK
        : FSC_COLORS.LOW_RISK;
      return [
        `& .${riskLevel.replace(/\s+/g, '-')}`,
        {
          backgroundColor,
          '&:hover': { backgroundColor }
        }
      ];
    })
  );
}


interface Row {
    id: string;
    description: string;
    risk: string;
}

export default function IndicatorDataGrid({ category, indicators, highRiskLevels = [] }: any) {
    const theme = useTheme()
    const [rows, setRows] = useState<Row[]>([])
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [modalText, setModalText] = useState<any>('');

    const handleCellClick = (event: any) => {
        event.stopPropagation()
    }
    const handleClick = (event: any, cellValues: any) => {
        event.stopPropagation()
        event.preventDefault();
        setModalText(cellValues.row.risk_notes)
        setOpenModal(true);
    }

    const handleClose = () => {

        setOpenModal(false);
        setModalText('')
    }

    useEffect(() => {
        setRows([])
        category.section.indicators.map((indicator: any) => {
            const i = indicators.find((x: any) => x.fsc_report_field.category_id === indicator.category_id &&
                x.fsc_report_field.indicator_id === indicator.indicator_id)
            if (!i) return;
            let entryRow = {
                "id": `${indicator.indicator_section_category.major_section_number}.${indicator.indicator_id}`,
                "description": indicator.description,
                "risk": i?.fsc_risk_level_option.risk_level,
                "risk_notes": i?.risk_notes
            }
            setRows((rows) => [...rows, entryRow]);
        })

    }, [])

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 70, align: 'center', headerAlign: 'center' },
        {
            field: 'description',
            headerName: 'Description',
            editable: false,
            sortable: false,
            flex: 1
        },
        {
            field: 'risk_notes',
            headerName: 'risk_notes',
        },
        {
            field: 'risk',
            headerName: 'Risk Level',
            editable: false,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            width: 98
        },
        {
            field: "Mitigation Measures",
            width: 140,
            align: 'center',
            headerAlign: 'center',
            renderCell: (cellValues) => {
                if (highRiskLevels.includes(cellValues.row.risk)) {
                    return (

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={(event) => {
                                handleClick(event, cellValues);
                            }}
                            sx={{
                                fontFamily: 'Roboto',
                                fontSize: '12px',
                                padding: '5px'
                            }}
                        >
                            Mitigation<br />Measures
                        </Button>
                    );
                } else {
                    return (

                        <Box />
                    );
                }

            }
        },

    ];


    return (
        <>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"

                >
                    <Box display='flex' flexDirection='row' alignItems='center' justifyContent='start' flexWrap='wrap'>
                        <Typography sx={{ marginRight: '5px' }}>{category.section.section_label} </Typography>
                        {
                            category?.section.indicators.map((indicator: any) => {
                                const i = indicators.find((x: any) =>
                                    x.fsc_report_field.category_id === indicator.category_id &&
                                    x.fsc_report_field.indicator_id === indicator.indicator_id
                                )
                                if (! i) return null;

                                const chipColor = highRiskLevels.includes(i.fsc_risk_level_option.risk_level)
                                    ? "warning"
                                    : "info";
                                return (
                                    <Chip
                                        size='small'
                                        sx={{
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            marginLeft: '1px',
                                            marginRight: '1px'
                                        }}
                                        label={`${indicator.indicator_section_category.major_section_number}.${indicator.indicator_id}`}
                                        color={chipColor}
                                    />
                                );
                            })
                        }
                    </Box>

                </AccordionSummary>
                <AccordionDetails>
                    <Typography><Box dangerouslySetInnerHTML={convertFromHtml(category.section_notes)} /></Typography>
                    <DataGrid
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    risk_notes: false
                                }
                            }
                        }}
                        rowHeight={70}
                        sx={{
                            marginTop: '10px',
                            width: '100%',
                            height: '400px',
                            ...generateRiskLevelStyles(indicators, highRiskLevels),
                            '&.Mui-selected': {
                                backgroundColor: "rebeccapurple",
                            }
                        }}

                        rows={rows}
                        columns={columns}
                        getRowClassName={(params) => `${params.row.risk.replace(/\s+/g, '-')}`}
                        onCellClick={(event) => { handleCellClick(event) }}
                        disableSelectionOnClick
                        disableColumnFilter
                        disableColumnMenu
                        hideFooter

                    />
                </AccordionDetails>
            </Accordion>
            <Modal
                open={openModal}
                onClose={() => handleClose()}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{
                    overflow: 'scroll'
                }}
            >
                <Box sx={modalStyle}>
                    <IconButton onClick={() => setOpenModal(false)} sx={{ position: 'absolute', top: '0', right: '0' }} aria-label="delete">
                        <CloseIcon />
                    </IconButton>
                    <Box dangerouslySetInnerHTML={convertFromHtml(modalText)} />
                </Box>
            </Modal >
        </>
    )
}
